import { notifications } from '@mantine/notifications';
import apiClient from './client';
import type { SessionContentParsedOutline } from './sessionContent';

/**
 * MED-976 (E1.3h) — Authored Session Content types served by the read-side
 * surfaces (coach prep, coach live, participant live). Mirror of the backend
 * shapes in medley-api/types/authoredAgenda.ts. Coach variants include
 * leader_prompts; participant variants do NOT (least-privilege at the type
 * layer — AC-PL3).
 */

export interface AuthoredAgendaLeaderPrompt {
  intro_text: string | null;
  /** Sanitized HTML; render via RichTextContent to preserve WYSIWYG (AC-CP3 / AC-CL1). */
  discussion_prompt: string | null;
  sort_order: number;
}

export interface AuthoredAgendaSectionVisual {
  /** 1-based PDF page number in the slide deck. */
  slide_page: number;
  sort_order: number;
}

export interface AuthoredAgendaSectionForCoach {
  id: string;
  section_name: string | null;
  duration_minutes: number | null;
  sort_order: number;
  /** Participant Prompt shown read-only to coaches (AC-CP4 / AC-CL4). */
  participant_prompt: string | null;
  /** MED-980 (E1.3e / AC-SCF) — section is the participant commitment-entry point in-session. MED-983 (E3.3a) consumes. */
  show_commitment_field: boolean;
  /** MED-980 (E1.3e / AC-SLC) — section displays the participant's prior-session commitment in-session. MED-983 (E3.3a) consumes. */
  show_last_session_commitment: boolean;
  leader_prompts: AuthoredAgendaLeaderPrompt[];
  visuals: AuthoredAgendaSectionVisual[];
}

export interface AuthoredAgendaSectionForParticipant {
  id: string;
  section_name: string | null;
  duration_minutes: number | null;
  sort_order: number;
  /** MED-980 (E1.3e / AC-PP) — Participant Prompt, sanitized HTML. MED-983 renders above slide or standalone. */
  participant_prompt: string | null;
  /** MED-980 (E1.3e / AC-SCF) — TRUE ⇒ show commitment entry field while this section is active (MED-983). */
  show_commitment_field: boolean;
  /** MED-980 (E1.3e / AC-SLC) — TRUE ⇒ show "Your Last Commitment" display in this section (MED-983). */
  show_last_session_commitment: boolean;
  visuals: AuthoredAgendaSectionVisual[];
}

export interface AuthoredAgendaIncompleteReasons {
  sections_missing_required_fields: boolean;
}

export interface AuthoredAgendaForCoach {
  /** AC-CP1 gate signal — true means coach prep must show the "not yet ready" Alert. */
  is_incomplete: boolean;
  incomplete_reasons: AuthoredAgendaIncompleteReasons;
  /**
   * MED-1182 (E1.3f-R1) — true when the session has ≥1 non-empty reflection
   * prompt. Drives the roster "Pre-session reflection" column (MED-1181 AC4),
   * replacing the removed `reflection_prompts_below_minimum` flag.
   */
  has_reflection_prompts: boolean;
  sections: AuthoredAgendaSectionForCoach[];
}

/**
 * MED-793 Coach Session Prep API client.
 *
 * Backend: medley-api/routes/coachSessionPrep.ts mounted under
 * /api/coach/sessions/:sessionId/...
 *
 * Mirrors the backend types in medley-api/types/coachSessionPrep.ts.
 * Note: backend Date columns serialize to ISO 8601 strings over the wire.
 */

// ---------------------------------------------------------------------------
// Type mirrors
// ---------------------------------------------------------------------------

export type CoachLeaderNotesScope = 'all_groups' | 'group';

export interface CoachSessionPrep {
  id: string;
  coach_user_id: string;
  session_id: string;
  marked_reviewed_at: string | null;
  rsvp_status: 'pending' | 'accepted' | 'declined' | 'tentative';
  rsvp_responded_at: string | null;
  rsvp_decline_reason: string | null;
  first_prep_completed_at: string | null;
  checklist_state: PrepChecklistState;
  /** MED-876: per-coach × per-session AI In Use selections, keyed by block_key. */
  ai_prompt_selections: AiPromptSelectionsDoc;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
}

/**
 * MED-876 — JSONB doc on coach_session_prep mirroring the backend type.
 * See medley-api/types/coachSessionPrep.ts for the full schema-on-read example.
 */
export type AiPromptSelectionsDoc = Record<string, AiPromptSegmentSelections>;

export interface AiPromptSegmentSelections {
  purpose?: AiPromptSlot;
  speaking?: AiPromptSlot;
  stall_recovery?: AiPromptSlot;
}

export interface AiPromptSlot {
  in_use: boolean;
  text: string;
  alternative_angle?: string | null;
}

export interface PrepChecklistState {
  reviewed_group_sentiment?: boolean;
  checked_agenda?: boolean;
  noted_carryover?: boolean;
  confirmed_attendance?: boolean;
}

export interface CoachSessionLeaderNote {
  id: string;
  coach_user_id: string;
  session_id: string;
  block_key: string;
  scope: CoachLeaderNotesScope;
  group_id: string | null;
  content_html: string;
  content_plain: string;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
}

/**
 * MED-1044 — one row in the GroupRosterCard per-participant list. The card
 * lists every participant assigned to the session with their own pre-session
 * reflection status (`reflection_completed`) and RSVP status. Mirrors the
 * backend `CoachPrepRosterParticipant`.
 */
export interface CoachPrepRosterParticipant {
  user_id: string;
  first_name: string;
  last_name: string;
  // MED-1162 — preferred name when set; the roster labels by preferred-or-first.
  preferred_name: string | null;
  reflection_completed: boolean;
  rsvp_status: 'pending' | 'accepted' | 'declined' | 'tentative' | null;
  /**
   * MED-1197 — true once invites have been sent (a session_attendance row
   * exists). Disambiguates the two `rsvp_status: null` cases so the roster
   * renders pre-invite state distinctly (AC3): false → not yet invited
   * ("Invitation pending" pill); true → invited, awaiting reply ("Awaiting").
   */
  invite_sent: boolean;
}

export interface CoachPrepGroupContext {
  /** The id of the group-specific session for THIS group (for MED-792 launch). */
  session_id: string;
  group_id: string;
  group_index: number | null;
  group_name: string;
  scheduled_start: string | null;
  scheduled_end: string | null;
  status_label: 'scheduled' | 'ready_to_start';
  /** MED-952 AC5 — true when this group's session is in_progress (show "Rejoin Session"). */
  is_live: boolean;
  participant_count: number;
  rsvp_accepted_count: number;
  prework_submitted_count: number;
  prework_total_count: number;
  has_missing_rsvps: boolean;
  has_missing_prework: boolean;
  /**
   * MED-1044 — every participant assigned to this group session, each with
   * their reflection + RSVP status, pre-sorted (not-complete first, then first
   * name). The counts above are derived from this list server-side (AC6).
   */
  participants: CoachPrepRosterParticipant[];
  /** MED-876 AC3 — per-group AI Listening state + derived lock flag. */
  ai_listening_enabled: boolean;
  ai_listening_locked: boolean;
}

/**
 * MED-976 (E1.3h) — Discriminated union for the coach prep agenda. The server
 * picks the mode based on the `live_session_delivery` feature flag (MED-886):
 *   - flag OFF → legacy parsed_outline shape (pre-e1.3d behavior)
 *   - flag ON  → authored sections from the e1.3d/e/f model
 *
 * Front-end branches on `mode` to render the correct accordion variant. When
 * `mode='authored'` and `authored.is_incomplete=true`, the page shows the
 * "Session content is still being prepared" Alert (AC-CP1) instead of the
 * agenda accordion.
 */
export type CoachPrepAgenda =
  | {
      mode: 'parsed_outline';
      parsed_outline: SessionContentParsedOutline | null;
      unavailable_reason?: 'no_content_uploaded' | 'still_processing' | 'failed';
    }
  | {
      mode: 'authored';
      authored: AuthoredAgendaForCoach;
    };

export interface CoachPrepPayload {
  session: {
    id: string;
    title: string;
    session_number: number | null;
    // MED-1103 — resolved internal topic for the MED-1085 header identity
    // ("Session N: {Topic}"); null when no topic is set.
    session_topic: string | null;
    // MED-1147 — participant display-name override + server-computed divergence
    // boolean for the internal-awareness indicator on the prep header.
    topic_display_name: string | null;
    participant_display_differs: boolean;
    /**
     * MED-1103 / MED-1181 — the cohort's group-coaching per-modality Live
     * Session Delivery toggle; `false` when the cohort has no active
     * group_coaching modality. MED-1181 uses it as the page-level LSD gate so
     * the whole prep experience (roster reflection column, checklist,
     * Start/Rejoin) fails closed for a classic MED01 cohort.
     */
    live_session_delivery_enabled: boolean;
    scheduled_start: string | null;
    scheduled_end: string | null;
    duration_minutes: number | null;
    cohort_id: string;
    cohort_name: string;
    organization_name: string | null;
    is_live: boolean;
    prep_unlocked: boolean;
  };
  /**
   * MED-1101 — resolved session-number description
   * (`session_number_config.description`) for the "Session Description" block
   * (AC4/AC7). Null when unset → block hidden (AC9).
   */
  description: string | null;
  agenda: CoachPrepAgenda;
  prep: CoachSessionPrep;
  leader_notes: CoachSessionLeaderNote[];
  groups: CoachPrepGroupContext[];
  ai_features_enabled: boolean;
  is_first_time_coach: boolean;
}

export interface SaveLeaderNoteRequest {
  block_key: string;
  scope: CoachLeaderNotesScope;
  group_id?: string | null;
  content_html: string;
}

export interface UpdateRsvpRequest {
  status: 'accepted' | 'declined' | 'tentative';
  decline_reason?: string;
}

// ---------------------------------------------------------------------------
// API surface
// ---------------------------------------------------------------------------

export const coachSessionPrepApi = {
  /** GET /api/coach/sessions/:sessionId/prep */
  getPrep: async (sessionId: string): Promise<CoachPrepPayload> => {
    return apiClient.get<CoachPrepPayload>(`/api/coach/sessions/${sessionId}/prep`);
  },

  /** POST mark this session reviewed (AC11). */
  markReviewed: async (sessionId: string): Promise<CoachSessionPrep> => {
    return apiClient.post<CoachSessionPrep>(
      `/api/coach/sessions/${sessionId}/prep/mark-reviewed`,
    );
  },

  /**
   * POST presence heartbeat (MED-1126). Keeps the coach 'present' in the
   * participant waiting room while they prep — coaches have no waiting room of
   * their own, so the prep page pings this on the waiting room's ~4s cadence.
   * Fire-and-forget: 204 No Content.
   */
  heartbeat: async (sessionId: string): Promise<void> => {
    await apiClient.post<void>(`/api/coach/sessions/${sessionId}/prep/heartbeat`);
  },

  /** DELETE — toggle off the reviewed state. */
  clearReviewed: async (sessionId: string): Promise<CoachSessionPrep> => {
    return apiClient.delete<CoachSessionPrep>(
      `/api/coach/sessions/${sessionId}/prep/mark-reviewed`,
    );
  },

  /** PUT one leader note. Empty content_html soft-deletes the row. */
  saveLeaderNote: async (
    sessionId: string,
    body: SaveLeaderNoteRequest,
  ): Promise<CoachSessionLeaderNote | null> => {
    return apiClient.put<CoachSessionLeaderNote | null>(
      `/api/coach/sessions/${sessionId}/leader-notes`,
      body,
    );
  },

  /** PUT coach RSVP for the session (AC2). */
  setRsvp: async (sessionId: string, body: UpdateRsvpRequest): Promise<CoachSessionPrep> => {
    return apiClient.put<CoachSessionPrep>(`/api/coach/sessions/${sessionId}/rsvp`, body);
  },

  /** PATCH "Before you start" checklist state. */
  patchChecklist: async (
    sessionId: string,
    patch: PrepChecklistState,
  ): Promise<CoachSessionPrep> => {
    return apiClient.patch<CoachSessionPrep>(
      `/api/coach/sessions/${sessionId}/prep/checklist`,
      { patch },
    );
  },

  /**
   * Open the print-ready prep sheet PDF (MED-793 / AC13) inline in a new tab.
   *
   * MED-1104: must go through the authenticated `apiClient` (NOT a bare
   * `window.open` on a relative path). `apiClient.openInNewTab` fetches the PDF
   * via the shared axios instance, so the request hits the correct API origin
   * (`env.apiUrl`) and carries the coach's bearer token — then opens the bytes
   * as an inline blob URL. The backend sends `Content-Disposition: inline`, so
   * it renders in-tab.
   *
   * On failure we branch on the HTTP status (attached to the thrown Error by the
   * response interceptor) so the coach sees a sensible message instead of a
   * silent failure or the generic app 404 (AC5). The error body arrives as a
   * Blob under `responseType: 'blob'`, so status — not message — is the reliable
   * signal here.
   */
  openPrepSheetPdf: async (sessionId: string): Promise<void> => {
    try {
      await apiClient.openInNewTab(`/api/coach/sessions/${sessionId}/prep-sheet.pdf`);
    } catch (error) {
      const status = (error as { status?: number }).status;
      let message: string;
      switch (status) {
        case 403:
          message = "You don't have access to this session's prep sheet.";
          break;
        case 404:
          message = 'This session could not be found. It may have been removed.';
          break;
        case 410:
          message = 'This session has started, so the prep sheet is no longer available.';
          break;
        default:
          message = 'We couldn’t open the prep sheet. Please try again.';
      }
      notifications.show({
        title: 'Unable to open prep sheet',
        message,
        color: 'red',
      });
    }
  },

  /** MED-876 AC3 — toggle AI Listening for a group's session. */
  setAiListening: async (
    sessionId: string,
    enabled: boolean,
  ): Promise<{ ai_listening_enabled: boolean; ai_listening_locked: boolean }> => {
    return apiClient.patch<{ ai_listening_enabled: boolean; ai_listening_locked: boolean }>(
      `/api/coach/sessions/${sessionId}/ai-listening`,
      { enabled },
    );
  },

  /** MED-876 AC2 / IV3 — replace the coach's In Use prompt selections doc. */
  patchAiSelections: async (
    sessionId: string,
    selections: AiPromptSelectionsDoc,
  ): Promise<{ ai_prompt_selections: AiPromptSelectionsDoc }> => {
    return apiClient.patch<{ ai_prompt_selections: AiPromptSelectionsDoc }>(
      `/api/coach/sessions/${sessionId}/prep/ai-selections`,
      { selections },
    );
  },
};
